/**
 * Models cache composable for the Playground feature
 * Manages loading and caching of available models
 */

import { ref, computed } from 'vue';
import { useApiError } from '@/features/auth/composables/useApiError';
import { useAuthStore } from '@/stores/auth';
import { useSessionStorage } from './useSessionStorage';
import { createOpenAIClient, fetchAvailableModels } from '../lib/openai-client';
import type { Model } from '../types/models';

// Cache expiry time (5 minutes)
const CACHE_EXPIRY_MS = 5 * 60 * 1000;

/**
 * Composable for managing models cache
 * Loads models on page mount and caches them for use across all tabs
 */
export function useModelsCache() {
  const authStore = useAuthStore();
  const { clearError, safeApiCall } = useApiError();
  const { session, loadSession, saveSession, createEmptySession } = useSessionStorage();

  // Initialize session if not loaded
  if (!session.value) {
    const loaded = loadSession();
    if (!loaded) {
      session.value = createEmptySession();
    }
  }

  // State
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  // Computed - get models from session cache
  const cachedModels = computed(() => session.value?.cachedModels || []);

  // Task-first filtering (model_task drives endpoint compatibility); falls
  // back to legacy capability flags for models not yet annotated with a task.
  const byTask = (task: string, capFallback?: (c: Model['capabilities']) => boolean) =>
    computed(() =>
      cachedModels.value.filter(m => {
        if (m.modelTask) return m.modelTask === task;
        return capFallback ? capFallback(m.capabilities) : false;
      })
    );

  const chatModels = byTask('chat', c => c.text);
  const textModels = chatModels;
  const visionModels = computed(() =>
    cachedModels.value.filter(m => {
      if (m.modelTask && m.modelTask !== 'chat') return false;
      return m.capabilities.vision || (m.inputModalities?.includes('image') ?? false);
    })
  );
  const imageGenerationModels = byTask('image_generation', c => c.image);
  const sttModels = byTask('transcription', c => c.audio);
  const ttsModels = byTask('tts', c => c.tts);
  const audioModels = computed(() => [...sttModels.value, ...ttsModels.value]);
  const embeddingsModels = byTask('embedding', c => c.embeddings);
  const moderationModels = byTask('moderation', c => c.moderation);
  const videoModels = byTask('video_generation', c => c.video);

  /** Filter models for a given endpoint task. Respects `model_task` when set. */
  const modelsForTask = (task: string) =>
    computed(() => cachedModels.value.filter(m => m.modelTask === task));

  /**
   * Check if cache is still valid
   */
  const isCacheValid = (): boolean => {
    if (!session.value?.modelsLoadedAt || !session.value?.cachedModels.length) {
      return false;
    }
    const now = new Date().getTime();
    const loadedAt = new Date(session.value.modelsLoadedAt).getTime();
    return now - loadedAt < CACHE_EXPIRY_MS;
  };

  /**
   * Load models from API and cache them
   */
  const loadModels = async (forceRefresh = false): Promise<Model[]> => {
    // Return cached models if valid and not forcing refresh
    if (!forceRefresh && isCacheValid()) {
      return cachedModels.value;
    }

    // Need auth token
    if (!authStore.token) {
      error.value = 'Authentication required';
      return [];
    }

    isLoading.value = true;
    error.value = null;
    clearError();

    const result = await safeApiCall(
      async () => {
        const client = createOpenAIClient({
          token: authStore.token!,
          isApiKey: false,
        });
        const models = await fetchAvailableModels(client);

        // Convert API model format to our Model type
        return models.map(model => {
          const capabilities = model.capabilities || [];

          return {
            id: model.id,
            name: model.id,
            description: undefined,
            capabilities: {
              text: capabilities.includes('text'),
              vision: capabilities.includes('vision'),
              image: capabilities.includes('image'),
              audio: capabilities.includes('audio'),
              tts: capabilities.includes('tts'),
              embeddings: capabilities.includes('embeddings'),
              video: capabilities.includes('video'),
              moderation: capabilities.includes('moderation'),
              streaming: capabilities.includes('text'),
            },
            modelTask: model.model_task,
            inputModalities: model.input_modalities,
            outputModalities: model.output_modalities,
            metadata: model.metadata,
            pricing: {
              inputCostPer1M: model.input_cost_1m,
              outputCostPer1M: model.output_cost_1m,
              pricingTiers: model.pricing_tiers?.map(t => ({
                upToTokens: t.up_to_tokens,
                inputPer1M: t.input_per_1m,
                outputPer1M: t.output_per_1m,
              })),
              perImageCost: model.per_image_cost,
              perSecondCost: model.per_second_cost,
              perAudioMinuteCost: model.per_audio_minute_cost,
              perCharCost: model.per_char_cost,
              audioInputCostPer1M: model.audio_input_cost_1m,
            },
          };
        });
      },
      {
        context: 'Load Models',
        retryOptions: {
          maxRetries: 3,
          initialDelay: 1000,
        },
      }
    );

    isLoading.value = false;

    if (result) {
      // Update session cache
      if (session.value) {
        session.value.cachedModels = result;
        session.value.modelsLoadedAt = new Date();
        session.value.timestamp = new Date();
        saveSession(session.value);
      }
      return result;
    } else {
      error.value = 'Failed to load models. Please try again.';
      return [];
    }
  };

  /**
   * Clear the models cache
   */
  const clearCache = (): void => {
    if (session.value) {
      session.value.cachedModels = [];
      session.value.modelsLoadedAt = undefined;
      saveSession(session.value);
    }
  };

  return {
    // State
    isLoading: computed(() => isLoading.value),
    error: computed(() => error.value),

    // All models
    cachedModels,

    // Filtered models by task/capability
    chatModels,
    textModels,
    visionModels,
    imageGenerationModels,
    audioModels,
    ttsModels,
    sttModels,
    embeddingsModels,
    moderationModels,
    videoModels,

    // Methods
    loadModels,
    clearCache,
    isCacheValid,
    modelsForTask,
  };
}
